import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter } from "../../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Tooltip } from "../../ui/tooltip";
import { InfoIcon, Loader2Icon } from "lucide-react";
import { DatasetService } from "../../../services/DatasetService";
import { ApiKeyOrGetter } from "../../../services/GeneralService";
import { toast } from "sonner";

type ReviewType = "ai" | "human" | "mixed";

interface AIReviewPayload {
  review_type: ReviewType;
  instructions?: string;
  confidence_threshold?: number;
}

interface ReviewEstimate {
  review_type: ReviewType;
  total_items: number;
  estimated_completion_time: number;
  maximum_allowed_time: number;
  reward_per_participant: number;
  estimated_total_cost: number;
}

interface AIReviewProps {
  isVisible: boolean;
  onClose: () => void;
  onStartReview: (payload: AIReviewPayload) => void;
  loading: boolean;
  datasetId?: number;
  apiKey: ApiKeyOrGetter;
  humanReviewInProgress?: boolean;
  humanReviewStatus?: string;
}

const AIReview: React.FC<AIReviewProps> = ({
  isVisible,
  onClose,
  onStartReview,
  loading,
  datasetId,
  apiKey,
  humanReviewInProgress,
  humanReviewStatus,
}) => {
  const [reviewType, setReviewType] = useState<ReviewType>("ai");
  const [instructions, setInstructions] = useState<string>("");
  const [isGeneratingInstructions, setIsGeneratingInstructions] =
    useState(false);
  const [reviewEstimate, setReviewEstimate] = useState<ReviewEstimate | null>(
    null
  );
  const [isReviewEstimateLoading, setIsReviewEstimateLoading] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const humanReviewDisabled = Boolean(humanReviewInProgress);
  const humanReviewDisabledReason = humanReviewStatus
    ? `Human review already in progress (${humanReviewStatus}).`
    : "Human review already in progress.";

  useEffect(() => {
    if (isVisible) {
      setReviewType("ai");
      setInstructions("");
      setReviewEstimate(null);
      setCurrentStepIndex(0);
    }
  }, [isVisible]);

  const handleStartReview = () => {
    const payload: AIReviewPayload = {
      review_type: reviewType,
    };
    if (reviewType !== "ai") {
      payload.instructions = instructions.trim();
    }
    onStartReview(payload);
  };

  const handleGenerateInstructions = () => {
    if (!datasetId) return;
    DatasetService.distillReviewInstructions(
      datasetId,
      setIsGeneratingInstructions,
      apiKey
    ).subscribe(
      (response: { instructions?: string }) => {
        setInstructions(response?.instructions || "");
      },
      (error: any) => {
        console.error("Failed to fetch prompt:", error);
        toast.error("Failed to generate instructions");
      }
    );
  };

  const hasInstructions = instructions.trim().length > 0;
  const canStartReview = reviewType === "ai" ? true : hasInstructions;

  const stepOrder = [
    "review_type",
    ...(reviewType !== "ai" ? ["instructions"] : []),
  ];
  const currentStep = stepOrder[currentStepIndex] || stepOrder[0];
  const isLastStep = currentStepIndex === stepOrder.length - 1;
  const canProceed = currentStep === "instructions" ? hasInstructions : true;

  useEffect(() => {
    if (currentStepIndex > stepOrder.length - 1) {
      setCurrentStepIndex(stepOrder.length - 1);
    }
  }, [currentStepIndex, stepOrder.length]);

  const formatCurrency = (cents: number) =>
    `$${(cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatMinutesAsHours = (minutes: number) => {
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return "0 minutes";
    }
    if (minutes < 60) {
      const roundedMinutes = Math.max(1, Math.round(minutes));
      return `${roundedMinutes} ${roundedMinutes === 1 ? "minute" : "minutes"}`;
    }
    const hours = Math.max(1, Math.round(minutes / 60));
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  };

  useEffect(() => {
    if (
      humanReviewDisabled &&
      (reviewType === "human" || reviewType === "mixed")
    ) {
      setReviewType("ai");
    }
  }, [humanReviewDisabled, reviewType]);

  useEffect(() => {
    if (!datasetId || reviewType !== "human") {
      setReviewEstimate(null);
      return;
    }
    DatasetService.getDatasetReviewEstimate(
      datasetId,
      { review_type: reviewType },
      setIsReviewEstimateLoading,
      apiKey
    ).subscribe(
      (response: ReviewEstimate) => {
        setReviewEstimate(response || null);
      },
      () => {
        setReviewEstimate(null);
      }
    );
  }, [datasetId, reviewType, apiKey]);

  return (
    <Dialog open={isVisible} onOpenChange={!loading ? onClose : undefined}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background border-border">
        <div className="space-y-6">
          {currentStep === "review_type" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <InfoIcon className="w-4 h-4 text-maitai-green" />
                Review Type
              </div>
              <RadioGroup
                value={reviewType}
                onValueChange={(value: ReviewType) => setReviewType(value)}
                className="space-y-4">
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="ai" id="review-ai" />
                  <div className="space-y-1">
                    <Label
                      htmlFor="review-ai"
                      className="text-sm font-gc-med text-foreground cursor-pointer">
                      AI
                    </Label>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      Automated review with corrections
                    </p>
                  </div>
                </div>
                {humanReviewDisabled ? (
                  <Tooltip content={humanReviewDisabledReason}>
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem
                        value="human"
                        id="review-human"
                        disabled={humanReviewDisabled}
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="review-human"
                          className="text-sm font-gc-med text-foreground cursor-pointer">
                          Human Expert
                        </Label>
                        <p className="text-xs text-muted-foreground font-gc-reg">
                          Expert annotation with detailed instructions
                        </p>
                      </div>
                    </div>
                  </Tooltip>
                ) : (
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="human" id="review-human" />
                    <div className="space-y-1">
                      <Label
                        htmlFor="review-human"
                        className="text-sm font-gc-med text-foreground cursor-pointer">
                        Human Expert
                      </Label>
                      <p className="text-xs text-muted-foreground font-gc-reg">
                        Expert annotation with detailed instructions
                      </p>
                    </div>
                  </div>
                )}
                {humanReviewDisabled ? (
                  <Tooltip content={humanReviewDisabledReason}>
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem
                        value="mixed"
                        id="review-mixed"
                        disabled={humanReviewDisabled}
                      />
                      <div className="space-y-1">
                        <Label
                          htmlFor="review-mixed"
                          className="text-sm font-gc-med text-foreground cursor-pointer">
                          Mix
                        </Label>
                        <p className="text-xs text-muted-foreground font-gc-reg">
                          AI review with low-confidence human fallback
                        </p>
                      </div>
                    </div>
                  </Tooltip>
                ) : (
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="mixed" id="review-mixed" />
                    <div className="space-y-1">
                      <Label
                        htmlFor="review-mixed"
                        className="text-sm font-gc-med text-foreground cursor-pointer">
                        Mix
                      </Label>
                      <p className="text-xs text-muted-foreground font-gc-reg">
                        AI review with low-confidence human fallback
                      </p>
                    </div>
                  </div>
                )}
              </RadioGroup>
            </div>
          )}

          {currentStep === "instructions" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-gc-med text-foreground">
                  Instructions for reviewers
                </Label>
                <Button
                  onClick={handleGenerateInstructions}
                  variant="outline"
                  size="sm"
                  className="gap-2 mr-6"
                  disabled={!datasetId || isGeneratingInstructions}>
                  {isGeneratingInstructions && (
                    <Loader2Icon className="w-4 h-4 animate-spin" />
                  )}
                  Generate Instructions
                </Button>
              </div>
              <Textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Add the guidance a human reviewer should follow..."
                className="min-h-[180px] font-mono text-sm"
              />
              {reviewEstimate && reviewType === "human" && (
                <div className="text-sm text-foreground font-gc-reg">
                  Estimated total cost:{" "}
                  {formatCurrency(reviewEstimate.estimated_total_cost)}
                </div>
              )}
              {reviewEstimate && reviewType === "human" && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Cost too high?{" "}
                  <button
                    type="button"
                    className="underline underline-offset-4"
                    onClick={() => {
                      setReviewType("ai");
                      setCurrentStepIndex(0);
                    }}>
                    Switch to AI review or Mix
                  </button>
                </div>
              )}
              {isReviewEstimateLoading && !reviewEstimate && (
                <div className="text-xs text-muted-foreground font-gc-reg">
                  Calculating estimated cost...
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          {currentStepIndex > 0 && (
            <Button
              variant="outline"
              onClick={() => setCurrentStepIndex((prev) => prev - 1)}
              disabled={loading}>
              Back
            </Button>
          )}
          <Button
            onClick={
              isLastStep
                ? handleStartReview
                : () => setCurrentStepIndex((prev) => prev + 1)
            }
            disabled={loading || (isLastStep ? !canStartReview : !canProceed)}
            className="gap-2">
            {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            {isLastStep ? "Start Review" : "Next"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIReview;
